import React, { useEffect } from 'react';

interface PhoneInputProps {
  countryCode: string;
  phoneNumber: string;
  onCountryCodeChange: (code: string) => void;
  onPhoneNumberChange: (number: string) => void;
  placeholder?: string;
  required?: boolean;
  className?: string;
}

// Helper to get saved country code preference
const getSavedCountryCode = (): string => {
  if (typeof window !== 'undefined') {
    return localStorage.getItem('preferredCountryCode') || '+52';
  }
  return '+52';
};

// Helper to save country code preference
const saveCountryCode = (code: string) => {
  if (typeof window !== 'undefined') {
    localStorage.setItem('preferredCountryCode', code);
  }
};

export default function PhoneInput({
  countryCode,
  phoneNumber,
  onCountryCodeChange,
  onPhoneNumberChange,
  placeholder = "10 dígitos",
  required = false,
  className = ""
}: PhoneInputProps) {
  // Load saved preference on mount if no country code is set
  useEffect(() => {
    if (countryCode === '+52' && phoneNumber === '') {
      const savedCode = getSavedCountryCode();
      if (savedCode !== countryCode) {
        onCountryCodeChange(savedCode);
      }
    }
  }, []);

  const handleCountryCodeChange = (newCode: string) => {
    saveCountryCode(newCode); // Save preference
    onCountryCodeChange(newCode);
  };

  const handlePhoneChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '');
    if (value.length <= 10) {
      onPhoneNumberChange(value);
    }
  };

  const formatPhoneDisplay = (phone: string) => {
    if (phone.length <= 3) return phone;
    if (phone.length <= 6) return `${phone.slice(0, 3)}-${phone.slice(3)}`;
    return `${phone.slice(0, 3)}-${phone.slice(3, 6)}-${phone.slice(6)}`;
  };

  return (
    <div className={`flex ${className}`}>
      <select
        value={countryCode}
        onChange={(e) => handleCountryCodeChange(e.target.value)}
        className="px-3 py-2 border border-r-0 border-gray-300 rounded-l-lg focus:outline-none focus:ring-2 focus:ring-green-500 bg-gray-50"
        required={required}
      >
        <option value="+52">🇲🇽 +52</option>
        <option value="+1">🇺🇸 +1</option>
      </select>
      <input
        type="tel"
        value={formatPhoneDisplay(phoneNumber)}
        onChange={handlePhoneChange}
        placeholder={placeholder}
        required={required}
        className="flex-1 px-3 py-2 border border-gray-300 rounded-r-lg focus:outline-none focus:ring-2 focus:ring-green-500"
      />
    </div>
  );
}